import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ProductSalesRow } from '@order-pilot/shared';
import { AsyncSection } from '@/components/AsyncSection';
import { PageHeader } from '@/components/PageHeader';
import {
  ReportColumnsButton,
  ReportFiltersButton,
  ReportSearchField,
  ReportToolbar,
} from '@/components/ReportToolbar';
import { SectionCard } from '@/components/SectionCard';
import { ErrorState } from '@/components/States';
import { applyReportLayout, DataTable, TableSkeleton } from '@/components/Table';
import type { Column } from '@/components/Table';
import { TablePager } from '@/components/TablePager';
import { KpiGrid, KpiTile, KpiTileSkeleton } from '@/features/portal/KpiTile';
import { ExportMenu } from '@/features/portal/ExportMenu';
import { usePortal } from '@/features/portal/PortalContext';
import {
  fetchProductSales,
  fetchProductTrend,
  portalKeys,
} from '@/features/portal/api';
import { toReportRange } from '@/features/portal/filters';
import { applyClientFilters } from '@/features/reports/applyClientFilters';
import { useReportLayout } from '@/features/reports/useReportLayout';
import { useReportToolbar } from '@/features/reports/useReportToolbar';
import { formatCount, formatMoney } from '@/lib/format';

const PAGE_SIZE = 15;

const FILTER_FIELDS = ['Product', 'Category'] as const;

const TOGGLEABLE = [
  'category',
  'quantitySold',
  'quantityReturned',
  'quantityNet',
  'revenue',
  'refunded',
  'revenueNet',
  'orders',
] as const;

const COLUMN_LABELS: Record<string, string> = {
  category: 'Category',
  quantitySold: 'Qty sold',
  quantityReturned: 'Qty returned',
  quantityNet: 'Net qty',
  revenue: 'Revenue',
  refunded: 'Refunded',
  revenueNet: 'Net revenue',
  orders: 'Orders',
};

/**
 * What each product sold over the range — every product that moved, not a
 * top-N leaderboard, so a slow seller is findable rather than merely absent.
 *
 * Returns are shown beside sales rather than folded into one figure: a
 * product with heavy returns and a product that simply sold less are very
 * different problems, and a single net number cannot tell them apart.
 */
export function ProductSalesPage() {
  const { currency, filters } = usePortal();
  const [searchParams, setSearchParams] = useSearchParams();
  const range = useMemo(() => toReportRange(filters.filters), [filters.filters]);
  const toolbar = useReportToolbar();
  const layout = useReportLayout('product-sales', TOGGLEABLE);
  const [selected, setSelected] = useState<string | null>(null);

  const salesQuery = useQuery({
    queryKey: portalKeys.productSales(range),
    queryFn: ({ signal }) => fetchProductSales(range, signal),
  });

  const trendQuery = useQuery({
    queryKey: portalKeys.productTrend(range, selected ?? ''),
    queryFn: ({ signal }) => fetchProductTrend(range, selected as string, signal),
    // Only fetched once a product is picked — the list is the default view.
    enabled: selected !== null,
  });

  const report = salesQuery.data;
  const reportCurrency = report?.currency || currency;

  const rows = useMemo(
    () =>
      applyClientFilters(report?.rows ?? [], {
        search: toolbar.search,
        filterRows: toolbar.filterRows,
        matchAny: toolbar.matchAny,
        fieldValue: (row, field) =>
          field === 'Product'
            ? row.productName
            : field === 'Category'
              ? row.categoryName
              : undefined,
        searchValues: (row) => [row.productName, row.categoryName],
      }),
    [report, toolbar.search, toolbar.filterRows, toolbar.matchAny],
  );

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  // Clamped rather than trusted: narrowing the date range or typing in the
  // search box shortens the list, and a stale `page=9` would otherwise render
  // an empty table over a populated report.
  const rawPage = Number.parseInt(searchParams.get('page') ?? '1', 10);
  const page = Number.isFinite(rawPage)
    ? Math.min(Math.max(1, rawPage), totalPages)
    : 1;

  const setPage = useCallback(
    (next: number) => {
      const params = new URLSearchParams(searchParams);
      // Page 1 is the default; keeping it out of the URL stops every shared
      // link carrying a redundant `page=1`.
      if (next <= 1) params.delete('page');
      else params.set('page', String(next));
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // Paged AFTER filtering, so search and the Filters popover still see every
  // product rather than only the page in view.
  const visible = useMemo(
    () => rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [rows, page],
  );

  const baseColumns = useMemo<Array<Column<ProductSalesRow>>>(() => {
    const money = (value: string) => formatMoney(value, reportCurrency);
    /** Zero returns are the normal case — a dash reads quieter than "0". */
    const none = <span className="text-slate-300">—</span>;

    return [
      {
        key: 'productName',
        header: 'Product',
        pinned: true,
        cell: (row) => (
          <button
            type="button"
            onClick={() => setSelected(row.productName)}
            className="text-left font-medium text-slate-900 underline decoration-slate-300 underline-offset-2 hover:decoration-slate-900"
          >
            {row.productName}
          </button>
        ),
      },
      {
        key: 'category',
        header: 'Category',
        cell: (row) => <span className="text-slate-500">{row.categoryName || '—'}</span>,
      },
      {
        key: 'quantitySold',
        header: 'Qty sold',
        align: 'right',
        cellClassName: 'tabular-nums',
        cell: (row) => formatCount(row.quantitySold),
      },
      {
        key: 'quantityReturned',
        header: 'Qty returned',
        align: 'right',
        cellClassName: 'tabular-nums',
        cell: (row) =>
          row.quantityReturned === 0 ? none : (
            <span className="text-rose-600">{formatCount(row.quantityReturned)}</span>
          ),
      },
      {
        key: 'quantityNet',
        header: 'Net qty',
        align: 'right',
        cellClassName: 'tabular-nums font-medium text-slate-900',
        // Negative means more came back this period than went out — real
        // movement, so it is shown rather than clamped to zero.
        cell: (row) => formatCount(row.quantityNet),
      },
      {
        key: 'revenue',
        header: 'Revenue',
        align: 'right',
        cellClassName: 'tabular-nums',
        cell: (row) => money(row.revenue),
      },
      {
        key: 'refunded',
        header: 'Refunded',
        align: 'right',
        cellClassName: 'tabular-nums',
        cell: (row) =>
          row.refunded === '0.00' ? none : (
            <span className="text-rose-600">{money(row.refunded)}</span>
          ),
      },
      {
        key: 'revenueNet',
        header: 'Net revenue',
        align: 'right',
        cellClassName: 'tabular-nums font-medium text-slate-900',
        cell: (row) => money(row.revenueNet),
      },
      {
        key: 'orders',
        header: 'Orders',
        align: 'right',
        cellClassName: 'tabular-nums',
        cell: (row) => formatCount(row.orders),
      },
    ];
  }, [reportCurrency]);

  const columns = useMemo(
    () => applyReportLayout(baseColumns, layout),
    [baseColumns, layout],
  );

  const header = (
    <PageHeader
      title="Product sales"
      description="What each product sold over the selected range, with returns netted off. Pick a product to see it over time."
      actions={
        <ExportMenu
          report="products"
          range={range}
          disabled={salesQuery.isPending || salesQuery.isError}
        />
      }
    />
  );

  if (salesQuery.isError) {
    return (
      <>
        {header}
        <div className="card">
          <ErrorState
            error={salesQuery.error}
            onRetry={() => void salesQuery.refetch()}
            retrying={salesQuery.isFetching}
          />
        </div>
      </>
    );
  }

  return (
    <>
      {header}

      <div className="space-y-6">
        <AsyncSection
          state={salesQuery}
          skeleton={
            <KpiGrid>
              {Array.from({ length: 4 }).map((_, index) => (
                <KpiTileSkeleton key={index} />
              ))}
            </KpiGrid>
          }
        >
          {(data) => (
            <KpiGrid>
              <KpiTile label="Products sold" value={formatCount(data.count)} />
              <KpiTile label="Items sold" value={formatCount(data.totalQuantitySold)} />
              <KpiTile
                label="Items returned"
                value={formatCount(data.totalQuantityReturned)}
              />
              <KpiTile
                label="Net revenue"
                value={formatMoney(data.totalRevenueNet, data.currency || currency)}
              />
            </KpiGrid>
          )}
        </AsyncSection>

        {selected !== null && (
          <SectionCard
            headingId="product-trend"
            title={selected}
            description="Quantity and revenue over the selected range."
            action={
              <button type="button" className="btn-ghost" onClick={() => setSelected(null)}>
                Close
              </button>
            }
          >
            <AsyncSection
              state={trendQuery}
              skeleton={<TableSkeleton rows={4} columns={3} />}
              isEmpty={(data) => data.points.length === 0}
              emptyTitle={`No sales of ${selected} in this range`}
              emptyDescription="Widen the date range, or clear the branch and till filters."
            >
              {(data) => (
                <DataTable
                  caption={`${selected} by ${data.granularity === 'daily' ? 'day' : 'month'}`}
                  columns={[
                    {
                      key: 'bucket',
                      header: data.granularity === 'daily' ? 'Date' : 'Month',
                      pinned: true,
                      cell: (point) => point.bucket,
                    },
                    {
                      key: 'quantity',
                      header: 'Qty',
                      align: 'right',
                      cellClassName: 'tabular-nums',
                      cell: (point) => formatCount(point.quantity),
                    },
                    {
                      key: 'revenue',
                      header: 'Revenue',
                      align: 'right',
                      cellClassName: 'tabular-nums font-medium text-slate-900',
                      cell: (point) => formatMoney(point.revenue, reportCurrency),
                    },
                  ]}
                  rows={data.points}
                  rowKey={(point) => point.bucket}
                />
              )}
            </AsyncSection>
          </SectionCard>
        )}

        <SectionCard
          headingId="product-sales-table"
          title="Products sold"
          count={rows.length}
          description="Every product that moved in this range. Click a product for its trend."
        >
          <ReportToolbar>
            <ReportSearchField
              value={toolbar.search}
              onChange={toolbar.setSearch}
              placeholder="Product or category"
            />
            <ReportFiltersButton
              fields={FILTER_FIELDS}
              applied={toolbar.filterRows}
              matchAny={toolbar.matchAny}
              onApply={toolbar.applyFilters}
            />
            <ReportColumnsButton layout={layout} labels={COLUMN_LABELS} />
          </ReportToolbar>
          <AsyncSection
            state={salesQuery}
            skeleton={<TableSkeleton rows={6} columns={7} />}
            isEmpty={() => rows.length === 0}
            emptyTitle={
              toolbar.search
                ? `No products match “${toolbar.search}”`
                : 'Nothing sold in this range'
            }
            emptyDescription="Products appear here once a till has synced sales for these dates."
          >
            {() => (
              <DataTable
                caption="Products with quantity sold, returned, net and revenue"
                columns={columns}
                rows={visible}
                rowKey={(row) => row.productName}
                layout={layout}
              />
            )}
          </AsyncSection>

          {!salesQuery.isPending && !salesQuery.isError ? (
            <TablePager
              page={page}
              pageSize={PAGE_SIZE}
              total={rows.length}
              onPageChange={setPage}
              busy={salesQuery.isFetching}
              noun="product"
            />
          ) : null}
        </SectionCard>
      </div>
    </>
  );
}
